import { useState } from "react";
import { Plus } from "../../../components/icons";
import { Button } from "../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../../../components/ui/dialog";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { useLanguage } from "../../../contexts/LanguageContext";
import { financialLabels } from "../labels";
import { api } from "../../../api";
import { toast } from "sonner";

export function AddIncomeDialog({ onSuccess }: { onSuccess: () => void }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { lang } = useLanguage();
  const t = financialLabels[lang];
  
  const [formData, setFormData] = useState({
    description: "",
    amount_cents: "",
    category: "Serviço",
    paid_at: new Date().toISOString().split('T')[0],
    payment_method: 'Pix'
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const amount = Math.floor(parseFloat(formData.amount_cents.replace(',', '.')) * 100);
    // Note: This logic assumes a generic client or uses a fallback for manual incomes
    const res = await api.createPayment({
      client_id: 0, // Fallback for manual/unlinked income if supported by backend
      amount_cents: amount,
      paid_at: formData.paid_at,
      notes: `Manual Income: ${formData.description} (${formData.category})`
    });
    setLoading(false);
    if (res.ok) {
      toast.success(lang === 'pt' ? "Entrada registrada" : "Income registered");
      setOpen(false);
      onSuccess();
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button 
          size="sm" 
          className="h-9 font-bold shadow-sm px-4 bg-green-600 hover:bg-green-700 text-white border-none"
        >
          <Plus className="mr-2 h-4 w-4" />
          {lang === 'pt' ? "Nova Entrada" : "New Income"}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{lang === 'pt' ? "Registrar Entrada Manual" : "Register Manual Income"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="in-desc">{lang === 'pt' ? "Descrição" : "Description"}</Label>
            <Input id="in-desc" placeholder="Ex: Venda de produto, Serviço extra..." required value={formData.description} onChange={e => setFormData(p => ({ ...p, description: e.target.value }))} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="in-amount">{lang === 'pt' ? "Valor (R$)" : "Amount"}</Label>
              <Input id="in-amount" placeholder="0,00" required value={formData.amount_cents} onChange={e => setFormData(p => ({ ...p, amount_cents: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>{lang === 'pt' ? "Método" : "Method"}</Label>
              <Select value={formData.payment_method} onValueChange={v => setFormData(p => ({ ...p, payment_method: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Pix">Pix</SelectItem>
                  <SelectItem value="Cartão">{t.methods.card}</SelectItem>
                  <SelectItem value="Dinheiro">{t.methods.cash}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="pt-4">
            <Button type="submit" variant="primary" disabled={loading} className="w-full font-bold">
              {lang === 'pt' ? "Confirmar Recebimento" : "Confirm Receipt"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
